import { memo, useCallback, useEffect, useMemo, useState, type UIEvent } from "react";

import type { CatalogProductMediaProjection } from "../runtime/gateway/catalog-price-client";
import { getPosRuntimePerformanceProfile } from "../runtime/performance/runtime-performance-profile";

type SaleGridMenu = {
  menuItemId: string;
  snapshot: {
    menu: {
      name: string;
      effective_card_display_name?: string | null;
      category_name?: string | null;
    };
    product_media?: {
      primary_media?: CatalogProductMediaProjection | null;
    } | null;
  };
};

type MediaUrlFor = (media: CatalogProductMediaProjection | null | undefined) => string | null;

type Props = {
  menus: SaleGridMenu[];
  mediaUrlFor: MediaUrlFor;
  onChooseMenu: (menuItemId: string) => void;
  showCategory: boolean;
};

type ProductCardProps = {
  entry: SaleGridMenu;
  mediaUrlFor: MediaUrlFor;
  onChooseMenu: (menuItemId: string) => void;
  showCategory: boolean;
};

const SaleProductCard = memo(function SaleProductCard({
  entry,
  mediaUrlFor,
  onChooseMenu,
  showCategory,
}: ProductCardProps) {
  const primaryMedia = entry.snapshot.product_media?.primary_media ?? null;
  const mediaUrl = mediaUrlFor(primaryMedia);
  const focalPoint = primaryMedia?.focal_point;
  const displayName = entry.snapshot.menu.effective_card_display_name?.trim() || entry.snapshot.menu.name;

  return (
    <button
      className={`sale-product-card ${primaryMedia ? "sale-product-card--media" : ""}`}
      type="button"
      onClick={() => onChooseMenu(entry.menuItemId)}
    >
      {primaryMedia ? (
        <span className="sale-product-card__media" data-fit-mode={primaryMedia.fit_mode}>
          {mediaUrl ? (
            <img
              src={mediaUrl}
              alt={primaryMedia.alt_text?.trim() || entry.snapshot.menu.name}
              draggable={false}
              loading="lazy"
              decoding="async"
              style={focalPoint ? {
                objectPosition: `${focalPoint.x * 100}% ${focalPoint.y * 100}%`,
              } : undefined}
            />
          ) : (
            <span className="sale-product-card__media-placeholder" aria-hidden="true">M</span>
          )}
        </span>
      ) : null}
      {showCategory && !primaryMedia ? (
        <span className="sale-product-card__category">{entry.snapshot.menu.category_name ?? "เมนู"}</span>
      ) : null}
      <strong>{displayName}</strong>
    </button>
  );
});

export const SaleProductGrid = memo(function SaleProductGrid({
  menus,
  mediaUrlFor,
  onChooseMenu,
  showCategory,
}: Props) {
  const performanceProfile = getPosRuntimePerformanceProfile();
  const batchSize = performanceProfile.productGridRenderBatchSize;
  const [visibleCount, setVisibleCount] = useState(() => (
    batchSize == null ? menus.length : Math.min(batchSize, menus.length)
  ));

  useEffect(() => {
    setVisibleCount(batchSize == null ? menus.length : Math.min(batchSize, menus.length));
  }, [batchSize, menus]);

  const visibleMenus = useMemo(
    () => batchSize == null ? menus : menus.slice(0, visibleCount),
    [batchSize, menus, visibleCount],
  );

  const loadNextBatch = useCallback((event: UIEvent<HTMLDivElement>) => {
    if (batchSize == null || visibleCount >= menus.length) return;
    const element = event.currentTarget;
    const remaining = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (remaining > 240) return;
    setVisibleCount((current) => Math.min(menus.length, current + batchSize));
  }, [batchSize, menus.length, visibleCount]);

  return (
    <div
      className={`sale-product-grid ${batchSize == null ? "" : "sale-product-grid--constrained"} ${showCategory ? "sale-product-grid--show-category" : ""}`}
      aria-label="สินค้าใน Published Local Catalog"
      onScroll={batchSize == null ? undefined : loadNextBatch}
    >
      {visibleMenus.map((entry) => (
        <SaleProductCard
          key={entry.menuItemId}
          entry={entry}
          mediaUrlFor={mediaUrlFor}
          onChooseMenu={onChooseMenu}
          showCategory={showCategory}
        />
      ))}
    </div>
  );
});
